// Deciding whether a locally-held session must be force-logged-out.
//
// Background: deleting a user runs `DELETE FROM auth.users`, which revokes server-side sessions,
// but the browser's access token (JWT) is stateless and keeps "working" for up to ~1h. So a just-
// deleted, still-logged-in user isn't signed out immediately — `getUser()` (an authoritative
// server check) starts failing while `getSession()` still returns a stale session. We call
// `getUser()` at bootstrap + on tab focus and, ONLY on a definitive "invalid session" signal,
// force a sign-out. The critical risk is logging everyone out on a transient network blip — so a
// retryable/network/5xx error must NEVER trigger logout.

import type { AuthError, User } from '@supabase/supabase-js';

export interface GetUserResult {
  data: { user: User | null };
  error: AuthError | null;
}

/**
 * True for errors that are NOT evidence of an invalid session — offline, CORS/DNS, request
 * aborted, gateway/server hiccups. On any of these we keep the session (retry later).
 */
export function isTransientAuthError(error: AuthError): boolean {
  const name = error.name ?? '';
  const status = (error as { status?: number }).status;
  if (name === 'AuthRetryableFetchError') return true; // supabase-js network/5xx wrapper
  if (status === undefined || status === 0) return true; // fetch failed before an HTTP status
  if (status >= 500) return true; // server-side hiccup, not a rejected credential
  return false;
}

/**
 * Decide whether the current session should be force-logged-out because the account no longer
 * exists / the session was invalidated. Returns true ONLY on a definitive signal; a transient
 * network error returns false (never log out on a blip).
 */
export function shouldForceLogout(result: GetUserResult): boolean {
  const { data, error } = result;

  if (error) {
    if (isTransientAuthError(error)) return false;
    const status = (error as { status?: number }).status;
    const code = (error as { code?: string }).code ?? '';
    // GoTrue answers a deleted/invalid subject with 401/403 (e.g. "User from sub claim ... does
    // not exist"). Treat those, and the known invalid-session codes, as definitive.
    if (status === 401 || status === 403) return true;
    if (['user_not_found', 'session_not_found', 'session_expired', 'bad_jwt'].includes(code)) return true;
    // Any other non-transient error (unexpected 4xx without a known auth code) → stay conservative.
    return false;
  }

  // No error, but the authoritative check returned no user while we believed we had a session →
  // the session no longer maps to a real account.
  return data.user == null;
}
